import { useState } from 'react'
import { gql } from 'graphql-request'
import { useNavigate } from 'react-router-dom'

import { useQuery, useMutation } from '@hooks/graphql'
import { useCurrentUser } from '@contexts/currentUser'
import EducatorDetails from './EducatorDetails'
import JoinOrCreateOrganization from './Organization/JoinOrCreateOrganization'
import CreateClassroom from './CreateClassroom'
import SplitLayout from '@/components/Onboarding/Layout/SplitLayout'
import Spinner from '@/components/Spinner'
import FlashMessage from '@/components/FlashMessage'

const ONBOARDING_QUERY = gql`
  query onboarding {
    me {
      profile {
        firstName
        lastName
        organizationRole
        phoneCountryCode
        phoneNumber
        locationCountryCode
        marketingSource
        subjects {
          id
          name
        }
        educatorOnboardingCompleted
        memberships {
          role
        }
      }
    }
  }
`

const COMPLETE_ONBOARDING_MUTATION = gql`
  mutation completeEducatorOnboarding {
    completeEducatorOnboarding(input: {}) {
      profile {
        educatorOnboardingCompleted
      }
    }
  }
`

const Onboarding = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState(null)
  const nextStep = () => { setStep(step + 1) }
  const previousStep = () => { setStep(step - 1) }

  const { refetchUser } = useCurrentUser()

  const {
    data: {
      me: { profile = {} } = {}
    } = {},
    isLoading
  } = useQuery({
    queryKey: ['onboardingData'],
    gqlQuery: ONBOARDING_QUERY,
    onSuccess: data => {
      // Onboarding already completed
      if (data.me.profile.educatorOnboardingCompleted) {
        navigate('/educators')
      } else {
        const profile = data.me.profile
        const hasCompletedEducatorDetails =
          profile.firstName &&
          profile.lastName &&
          profile.organizationRole &&
          profile.subjects?.length > 0 &&
          profile.locationCountryCode &&
          profile.phoneCountryCode &&
          profile.phoneNumber &&
          profile.marketingSource

        const hasMembership = profile.memberships.length > 0

        if (!hasCompletedEducatorDetails) {
          setStep(1)
        } else if (!hasMembership) {
          setStep(2)
        } else {
          setStep(3)
        }
      }
    }
  })

  const hasExistingMembership = profile.memberships?.length > 0

  const { mutate: completeOnboarding, isLoading: isCompleting } = useMutation({
    gqlMutation: COMPLETE_ONBOARDING_MUTATION,
    onSuccess: async () => {
      await refetchUser()

      const tutorId = localStorage.getItem('signUpTutorId')

      if (tutorId) {
        localStorage.removeItem('signUpTutorId')
        navigate(`/educators/tutors/${tutorId}`)
      } else {
        navigate('/educators')
      }
    }
  })

  return (
    <>
      <FlashMessage />

      <Choose>
        <When condition={isLoading || isCompleting}>
          <SplitLayout>
            <Spinner className='flex justify-center' />
          </SplitLayout>
        </When>

        <When condition={step === 1}>
          <EducatorDetails
            firstName={profile.firstName}
            lastName={profile.lastName}
            organizationRole={profile.organizationRole}
            subjects={profile.subjects}
            phoneCountryCode={profile.phoneCountryCode}
            phoneNumber={profile.phoneNumber}
            locationCountryCode={profile.locationCountryCode}
            marketingSource={profile.marketingSource}
            nextStep={nextStep}
          />
        </When>

        <When condition={step === 2}>
          <JoinOrCreateOrganization
            nextStep={nextStep}
            hasExistingMembership={hasExistingMembership}
          />
        </When>

        <When condition={step === 3}>
          <CreateClassroom
            nextStep={nextStep}
            completeOnboarding={completeOnboarding}
          />
        </When>
      </Choose>
    </>
  )
}

export default Onboarding
